import React from 'react';
import type { DockTab } from '../../types';
import { ValidationPanel } from './ValidationPanel';
import { RunMonitorPanel } from './RunMonitorPanel';
import { ProbeDataPanel } from './ProbeDataPanel';
import { TimeSeriesPanel } from './TimeSeriesPanel';
import { ComparisonPanel } from './ComparisonPanel';
import { ConsolePanel } from './ConsolePanel';

export interface ContextDockProps {
  activeTab: DockTab;
  onTabChange: (tab: DockTab) => void;
  isOpen: boolean;
  onToggleOpen: () => void;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

interface DockTabConfig {
  id: DockTab;
  label: string;
  badge?: string;
  subroute: string;
}

const DOCK_TABS: DockTabConfig[] = [
  { id: 'validation', label: 'Validation', subroute: '/simulation/validation' },
  { id: 'run-monitor', label: 'Run Monitor', subroute: '/simulation/run-monitor' },
  { id: 'probe-data', label: 'Probe Data', subroute: '/simulation/probe-data' },
  { id: 'time-series', label: 'Time Series', subroute: '/simulation/time-series' },
  { id: 'comparison', label: 'Comparison', subroute: '/simulation/comparison' },
  { id: 'console', label: 'Console', subroute: '/simulation/console' }
];

export const ContextDock: React.FC<ContextDockProps> = ({
  activeTab,
  onTabChange,
  isOpen,
  onToggleOpen,
  isExpanded = false,
  onToggleExpanded
}) => {
  return (
    <section
      className={`ssw-context-dock ${isOpen ? 'is-open' : 'is-collapsed'} ${isExpanded ? 'is-expanded' : ''}`}
      aria-label="Context and Analysis Dock"
    >
      {/* Dock Control Bar */}
      <div className="ssw-dock-bar">
        <div className="ssw-dock-tabs" role="tablist" aria-label="Analysis Dock Views">
          {DOCK_TABS.map((tab) => {
            const isActive = isOpen && activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                id={`ssw-dock-tab-${tab.id}`}
                aria-selected={isActive}
                aria-controls={`ssw-dock-panel-${tab.id}`}
                className={`ssw-dock-tab-btn ${isActive ? 'is-active' : ''}`}
                onClick={() => {
                  if (!isOpen) onToggleOpen();
                  onTabChange(tab.id);
                }}
              >
                <span className="ssw-dock-tab-label">{tab.label}</span>
                {tab.badge && <span className="ssw-dock-tab-badge">{tab.badge}</span>}
              </button>
            );
          })}
        </div>

        <div className="ssw-dock-actions">
          {onToggleExpanded && isOpen && (
            <button
              type="button"
              className="ssw-dock-icon-btn"
              onClick={onToggleExpanded}
              aria-label={isExpanded ? 'Restore dock height' : 'Maximize dock height'}
              title={isExpanded ? 'Restore dock height' : 'Maximize dock height'}
            >
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                {isExpanded ? (
                  <path d="M5 11L11 5M11 5H6M11 5V10" />
                ) : (
                  <path d="M3 8V3H8M13 8V13H8" />
                )}
              </svg>
            </button>
          )}

          <button
            type="button"
            className="ssw-dock-icon-btn"
            onClick={onToggleOpen}
            aria-label={isOpen ? 'Collapse analysis dock' : 'Expand analysis dock'}
            title={isOpen ? 'Collapse dock' : 'Open dock'}
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              {isOpen ? (
                <path d="M3 5L8 10L13 5" />
              ) : (
                <path d="M3 11L8 6L13 11" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Dock Content Stage */}
      {isOpen && (
        <div className="ssw-dock-stage">
          {activeTab === 'validation' && <ValidationPanel />}
          {activeTab === 'run-monitor' && <RunMonitorPanel />}
          {activeTab === 'probe-data' && <ProbeDataPanel />}
          {activeTab === 'time-series' && <TimeSeriesPanel />}
          {activeTab === 'comparison' && <ComparisonPanel />}
          {activeTab === 'console' && <ConsolePanel />}
        </div>
      )}
    </section>
  );
};

export default ContextDock;
